import { useEffect, useRef, useState, lazy, Suspense } from 'react';
const AiCodeEditor = lazy(() => import('./AiCodeEditor'));
import { useNavigate, useParams } from 'react-router-dom';
// ── GRAPESJS REMOVED ──────────────────────────────────────────────────────
// These imports were for the GrapesJS visual editor (removed 2026-06-18):
//   import grapesjs from 'grapesjs';
//   import grapesjsBlocksBasic from 'grapesjs-blocks-basic';
//   import grapesjsForms from 'grapesjs-plugin-forms';
//   import grapesjsTabs from 'grapesjs-tabs';
//   import 'grapesjs/dist/css/grapes.min.css';
//   import type { Editor } from 'grapesjs';
// ──────────────────────────────────────────────────────────────────────────
import {
  ArrowLeft,
  Save,
  Loader2,
  Layers,
  LayoutGrid,
  ChevronRight,
  Code2,
  FileStack,
  Image as ImageIcon,
  Palette,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Monitor,
  Tablet,
  Smartphone,
  Undo2,
  Redo2,
  Search,
  X,
  Sparkles,
  MessageSquare,
  SendHorizonal,
  Wand2,
  Mic,
  MicOff,
  Paperclip,
  X as XIcon,
  ArrowUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { wbSitesApi, wbPagesApi, wbAiApi, type WbSite, type WbPage } from '@/lib/api/services/websiteBuilder';
import { Textarea } from '@/components/ui/textarea';
import TemplateGallery from './TemplateGallery';

// ── [grapesjs REMOVED] These plugin functions were previously registered on
//     the grapesjs Editor instance. The block content definitions are preserved
//     below as reference, but the editor parameter type is changed to `any`
//     since the `Editor` type from grapesjs is no longer available.
function ecommercePlugin(editor: any) {
  editor.Blocks.add('hero-banner', {
    label: 'Hero Banner',
    media: '🖼️',
    category: 'Ecommerce',
    content: `
      <section style="min-height:420px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#121826,#2b3660);color:#fff;text-align:center;padding:48px 24px">
        <div style="max-width:680px">
          <h1 style="font-size:44px;font-weight:800;line-height:1.1;margin:0 0 16px">Welcome to Our Store</h1>
          <p style="opacity:0.85;font-size:18px;margin:0 0 24px">Discover our latest collection</p>
          <a href="/products" style="display:inline-block;background:#fff;color:#0f172a;padding:12px 24px;border-radius:9999px;text-decoration:none;font-weight:700">Shop Now</a>
        </div>
      </section>`,
  });
}

function basicLayoutBlocksPlugin(editor: any) {
  const category = 'Basic';
  const formsCategory = 'Forms';
  const extraCategory = 'Extra';
  const dataSourcesCategory = 'Data Sources';
  const layoutCategory = 'Layout';
  const advancedCategory = 'Advanced';

  editor.Blocks.add('wb-1-column', {
    label: '1 Column',
    category,
    content: `
      <section style="max-width:1200px;margin:0 auto;padding:24px;">
        <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">Column</div>
      </section>
    `,
  });

  editor.Blocks.add('wb-2-columns', {
    label: '2 Columns',
    category,
    content: `
      <section style="max-width:1200px;margin:0 auto;padding:24px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
          <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">Column 1</div>
          <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">Column 2</div>
        </div>
      </section>
    `,
  });

  editor.Blocks.add('wb-3-columns', {
    label: '3 Columns',
    category,
    content: `
      <section style="max-width:1200px;margin:0 auto;padding:24px;">
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;">
          <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">Column 1</div>
          <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">Column 2</div>
          <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">Column 3</div>
        </div>
      </section>
    `,
  });

  editor.Blocks.add('wb-2-columns-3-7', {
    label: '2 Columns 3/7',
    category,
    content: `
      <section style="max-width:1200px;margin:0 auto;padding:24px;">
        <div style="display:grid;grid-template-columns:3fr 7fr;gap:16px;">
          <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">3</div>
          <div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">7</div>
        </div>
      </section>
    `,
  });

  editor.Blocks.add('wb-section', {
    label: 'Section',
    category,
    content: `
      <section style="max-width:1200px;margin:0 auto;padding:40px 24px;">
        <h2 style="font-size:28px;font-weight:700;margin:0 0 10px;">Section Title</h2>
        <p style="margin:0;color:#475569;">Add section content here.</p>
      </section>
    `,
  });

  editor.Blocks.add('wb-divider', {
    label: 'Divider',
    category,
    content: `<hr style="border:0;border-top:1px solid #e2e8f0;margin:24px 0;" />`,
  });

  editor.Blocks.add('wb-heading', {
    label: 'Heading',
    category,
    content: `<h2 style="font-size:32px;line-height:1.2;font-weight:700;margin:0 0 12px;">Heading Text</h2>`,
  });

  editor.Blocks.add('wb-text', {
    label: 'Text',
    category,
    content: `<p style="margin:0;color:#334155;line-height:1.6;">Add your text here...</p>`,
  });

  editor.Blocks.add('wb-link', {
    label: 'Link',
    category,
    content: `<a href="#" style="color:#1ab169;text-decoration:none;">Link text</a>`,
  });

  editor.Blocks.add('wb-link-box', {
    label: 'Link Box',
    category,
    content: `<a href="#" style="display:inline-block;padding:10px 16px;border:1px solid #d1d5db;border-radius:8px;text-decoration:none;color:#111827;">Link Box</a>`,
  });

  editor.Blocks.add('wb-image', {
    label: 'Image',
    category,
    content: `<img src="https://images.unsplash.com/photo-1465101046530-73398c7f28ca?w=900" alt="Image" style="width:100%;height:auto;border-radius:8px;" />`,
  });

  editor.Blocks.add('wb-image-box', {
    label: 'Image Box',
    category,
    content: `
      <div style="border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;max-width:420px;background:#fff;">
        <img src="https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=900" alt="Card image" style="width:100%;height:auto;display:block;" />
        <div style="padding:12px 14px;">
          <h4 style="margin:0 0 4px;font-size:16px;">Image Box</h4>
          <p style="margin:0;color:#64748b;font-size:14px;">Caption or description</p>
        </div>
      </div>
    `,
  });

  editor.Blocks.add('wb-video', {
    label: 'Video',
    category,
    content: `
      <div style="position:relative;padding-top:56.25%;border-radius:10px;overflow:hidden;background:#0f172a;">
        <iframe
          src="https://www.youtube.com/embed/dQw4w9WgXcQ"
          title="Video"
          allowfullscreen
          style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
        ></iframe>
      </div>
    `,
  });

  editor.Blocks.add('wb-map', {
    label: 'Map',
    category,
    content: `
      <iframe
        title="Map"
        src="https://www.openstreetmap.org/export/embed.html?bbox=77.50%2C12.90%2C77.70%2C13.10&layer=mapnik"
        style="width:100%;height:320px;border:1px solid #e5e7eb;border-radius:10px;"
      ></iframe>
    `,
  });

  editor.Blocks.add('wb-icon', {
    label: 'Icon',
    category,
    content: `<span style="display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:9999px;background:#f1f5f9;font-size:20px;">⭐</span>`,
  });

  // Forms
  editor.Blocks.add('wb-form', {
    label: 'Form',
    category: formsCategory,
    content: `<form style="display:grid;gap:10px;max-width:420px;"><input placeholder="Name" style="padding:10px;border:1px solid #d1d5db;border-radius:8px;" /><input type="email" placeholder="Email" style="padding:10px;border:1px solid #d1d5db;border-radius:8px;" /><button style="padding:10px 14px;border:0;border-radius:8px;background:#111827;color:#fff;">Submit</button></form>`,
  });
  editor.Blocks.add('wb-form-input', {
    label: 'Input',
    category: formsCategory,
    content: `<input placeholder="Input" style="padding:10px;border:1px solid #d1d5db;border-radius:8px;" />`,
  });
  editor.Blocks.add('wb-form-textarea', {
    label: 'Textarea',
    category: formsCategory,
    content: `<textarea placeholder="Textarea" style="padding:10px;border:1px solid #d1d5db;border-radius:8px;min-height:110px;"></textarea>`,
  });
  editor.Blocks.add('wb-form-select', {
    label: 'Select',
    category: formsCategory,
    content: `<select style="padding:10px;border:1px solid #d1d5db;border-radius:8px;"><option>Option 1</option><option>Option 2</option></select>`,
  });
  editor.Blocks.add('wb-form-button', {
    label: 'Button',
    category: formsCategory,
    content: `<button style="padding:10px 14px;border:0;border-radius:8px;background:#111827;color:#fff;">Button</button>`,
  });
  editor.Blocks.add('wb-form-label', {
    label: 'Label',
    category: formsCategory,
    content: `<label style="font-weight:600;color:#334155;">Label</label>`,
  });
  editor.Blocks.add('wb-form-checkbox', {
    label: 'Checkbox',
    category: formsCategory,
    content: `<label style="display:flex;align-items:center;gap:8px;"><input type="checkbox" /> Checkbox</label>`,
  });
  editor.Blocks.add('wb-form-radio', {
    label: 'Radio',
    category: formsCategory,
    content: `<label style="display:flex;align-items:center;gap:8px;"><input type="radio" name="radio-group" /> Radio</label>`,
  });

  // Extra
  editor.Blocks.add('wb-navbar', {
    label: 'Navbar',
    category: extraCategory,
    content: `
      <nav style="display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border:1px solid #e5e7eb;border-radius:10px;background:#fff;color:#111827;">
        <strong style="color:#111827;">Brand</strong>
        <div style="display:flex;gap:14px;">
          <a href="#" style="text-decoration:none;color:#334155;">Home</a>
          <a href="#" style="text-decoration:none;color:#334155;">Products</a>
          <a href="#" style="text-decoration:none;color:#334155;">Contact</a>
        </div>
      </nav>
    `,
  });

  // Data sources placeholders
  editor.Blocks.add('wb-variable', {
    label: 'Variable',
    category: dataSourcesCategory,
    content: `<div data-variable="{{user.name}}" style="padding:8px 10px;border:1px dashed #94a3b8;border-radius:8px;background:#f8fafc;color:#111827;">{{ variable }}</div>`,
  });
  editor.Blocks.add('wb-condition', {
    label: 'Condition',
    category: dataSourcesCategory,
    content: `<div data-condition="if" style="padding:8px 10px;border:1px dashed #94a3b8;border-radius:8px;background:#f8fafc;color:#111827;">Condition block</div>`,
  });
  editor.Blocks.add('wb-collection', {
    label: 'Collection',
    category: dataSourcesCategory,
    content: `<ul data-collection="items" style="padding-left:18px;color:#111827;"><li>Collection item 1</li><li>Collection item 2</li></ul>`,
  });

  // Layout
  const layoutBlock = (id: string, label: string, template: string) => {
    editor.Blocks.add(id, { label, category: layoutCategory, content: template });
  };

  layoutBlock('wb-layout-1', '1 Column', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:1fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">1</div></div></section>`);
  layoutBlock('wb-layout-2-50-50', '2 Columns 50/50', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">50</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">50</div></div></section>`);
  layoutBlock('wb-layout-2-25-75', '2 Columns 25/75', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:1fr 3fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">75</div></div></section>`);
  layoutBlock('wb-layout-2-75-25', '2 Columns 75/25', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:3fr 1fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">75</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div></div></section>`);
  layoutBlock('wb-layout-3', '3 Columns', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">1</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">2</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">3</div></div></section>`);
  layoutBlock('wb-layout-3-50-25-25', '3 Columns 50/25/25', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:2fr 1fr 1fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">50</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div></div></section>`);
  layoutBlock('wb-layout-3-25-50-25', '3 Columns 25/50/25', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:1fr 2fr 1fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">50</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div></div></section>`);
  layoutBlock('wb-layout-3-25-25-50', '3 Columns 25/25/50', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:1fr 1fr 2fr;gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">25</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">50</div></div></section>`);
  layoutBlock('wb-layout-4', '4 Columns', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;"><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">1</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">2</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">3</div><div style="min-height:80px;border:1px dashed #cbd5e1;padding:16px;">4</div></div></section>`);
  layoutBlock('wb-layout-5', '5 Columns', `<section style="max-width:1200px;margin:0 auto;padding:24px;"><div style="display:grid;grid-template-columns:repeat(5,1fr);gap:12px;"><div style="min-height:72px;border:1px dashed #cbd5e1;padding:12px;">1</div><div style="min-height:72px;border:1px dashed #cbd5e1;padding:12px;">2</div><div style="min-height:72px;border:1px dashed #cbd5e1;padding:12px;">3</div><div style="min-height:72px;border:1px dashed #cbd5e1;padding:12px;">4</div><div style="min-height:72px;border:1px dashed #cbd5e1;padding:12px;">5</div></div></section>`);

  // Advanced extras requested
  editor.Blocks.add('wb-tabs', {
    label: 'Tabs',
    category: advancedCategory,
    content: () => {
      const uid = `wb-tabs-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const tab1 = `${uid}-tab-1`;
      const tab2 = `${uid}-tab-2`;
      return `
        <div class="wb-tabs" style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;background:#fff;color:#111827;">
          <style>
            .wb-tabs__input { position: absolute; opacity: 0; pointer-events: none; }
            .wb-tabs__headings { display: flex; border-bottom: 1px solid #e5e7eb; background: #f8fafc; }
            .wb-tabs__label { padding: 10px 14px; cursor: pointer; border-right: 1px solid #e5e7eb; color: #475569; background: #f8fafc; font-weight: 500; user-select: none; }
            .wb-tabs__label:last-child { border-right: 0; }
            .wb-tabs__panel { display: none; padding: 14px; color: #111827; line-height: 1.5; }
            #${tab1}:checked ~ .wb-tabs__headings label[for="${tab1}"] { background: #ffffff; color: #111827; font-weight: 600; }
            #${tab2}:checked ~ .wb-tabs__headings label[for="${tab2}"] { background: #ffffff; color: #111827; font-weight: 600; }
            #${tab1}:checked ~ .wb-tabs__panels .wb-tabs__panel--1 { display: block; }
            #${tab2}:checked ~ .wb-tabs__panels .wb-tabs__panel--2 { display: block; }
          </style>
          <input class="wb-tabs__input" type="radio" name="${uid}" id="${tab1}" checked />
          <input class="wb-tabs__input" type="radio" name="${uid}" id="${tab2}" />
          <div class="wb-tabs__headings">
            <label class="wb-tabs__label" for="${tab1}">Tab 1</label>
            <label class="wb-tabs__label" for="${tab2}">Tab 2</label>
          </div>
          <div class="wb-tabs__panels">
            <div class="wb-tabs__panel wb-tabs__panel--1">Tab 1 content</div>
            <div class="wb-tabs__panel wb-tabs__panel--2">Tab 2 content</div>
          </div>
        </div>
      `;
    },
  });
  editor.Blocks.add('wb-tooltip', {
    label: 'Tooltip',
    category: advancedCategory,
    content: `<span title="Tooltip text" style="display:inline-block;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;background:#fff;color:#111827;">Hover me</span>`,
  });
  editor.Blocks.add('wb-accordion', {
    label: 'Accordion',
    category: advancedCategory,
    content: `<details style="border:1px solid #e5e7eb;border-radius:8px;padding:10px;background:#fff;color:#111827;"><summary style="cursor:pointer;font-weight:600;color:#111827;">Accordion title</summary><p style="margin:10px 0 0;color:#334155;">Accordion content</p></details>`,
  });
  editor.Blocks.add('wb-slider', {
    label: 'Swiper (Slider)',
    category: advancedCategory,
    content: `<div style="display:flex;overflow:auto;gap:12px;padding:8px;"><div style="min-width:220px;height:120px;background:#e2e8f0;border-radius:10px;"></div><div style="min-width:220px;height:120px;background:#cbd5e1;border-radius:10px;"></div><div style="min-width:220px;height:120px;background:#94a3b8;border-radius:10px;"></div></div>`,
  });
  editor.Blocks.add('wb-list', {
    label: 'List',
    category: advancedCategory,
    content: `<ul style="padding-left:20px;color:#111827;"><li>List item one</li><li>List item two</li><li>List item three</li></ul>`,
  });
  editor.Blocks.add('wb-list-pages', {
    label: 'List Pages',
    category: advancedCategory,
    content: `<nav><ul style="display:grid;gap:8px;padding-left:20px;"><li><a href="/" style="text-decoration:none;color:#1ab169;">Home</a></li><li><a href="/about" style="text-decoration:none;color:#1ab169;">About</a></li><li><a href="/contact" style="text-decoration:none;color:#1ab169;">Contact</a></li></ul></nav>`,
  });
  editor.Blocks.add('wb-table', {
    label: 'Table',
    category: advancedCategory,
    content: `<table style="width:100%;border-collapse:collapse;background:#fff;color:#111827;"><thead><tr><th style="border:1px solid #e5e7eb;padding:8px;background:#f8fafc;color:#111827;">Name</th><th style="border:1px solid #e5e7eb;padding:8px;background:#f8fafc;color:#111827;">Value</th></tr></thead><tbody><tr><td style="border:1px solid #e5e7eb;padding:8px;color:#111827;">Row 1</td><td style="border:1px solid #e5e7eb;padding:8px;color:#111827;">Data</td></tr><tr><td style="border:1px solid #e5e7eb;padding:8px;color:#111827;">Row 2</td><td style="border:1px solid #e5e7eb;padding:8px;color:#111827;">Data</td></tr></tbody></table>`,
  });

  // Ensure every block has a clean SVG icon/media.
  const mediaIcon = (inner: string) => `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      ${inner}
    </svg>
  `;
  const icon1Col = mediaIcon(`<rect x="4" y="5" width="16" height="14" rx="1.5" stroke="currentColor" stroke-width="1.7"/>`);
  const icon2Col = mediaIcon(`<rect x="4" y="5" width="7.2" height="14" rx="1.2" stroke="currentColor" stroke-width="1.7"/><rect x="12.8" y="5" width="7.2" height="14" rx="1.2" stroke="currentColor" stroke-width="1.7"/>`);
  const icon3Col = mediaIcon(`<rect x="3.5" y="5" width="5.4" height="14" rx="1.1" stroke="currentColor" stroke-width="1.6"/><rect x="9.3" y="5" width="5.4" height="14" rx="1.1" stroke="currentColor" stroke-width="1.6"/><rect x="15.1" y="5" width="5.4" height="14" rx="1.1" stroke="currentColor" stroke-width="1.6"/>`);
  const iconSection = mediaIcon(`<path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>`);
  const iconDivider = mediaIcon(`<path d="M4 12h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M10 9l2-2 2 2M10 15l2 2 2-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`);
  const iconHeading = mediaIcon(`<path d="M6 6v12M18 6v12M6 12h12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>`);
  const iconText = mediaIcon(`<path d="M5 7h14M8 12h8M10 17h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>`);
  const iconLink = mediaIcon(`<path d="M10 8h-2.5a3.5 3.5 0 0 0 0 7H10M14 8h2.5a3.5 3.5 0 0 1 0 7H14M8.5 12h7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`);
  const iconImage = mediaIcon(`<rect x="4" y="5" width="16" height="14" rx="1.5" stroke="currentColor" stroke-width="1.7"/><circle cx="9" cy="10" r="1.4" fill="currentColor"/><path d="M6 16l3.5-3.5L12 15l2.2-2.2L18 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`);
  const iconVideo = mediaIcon(`<rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M11 9.8l4 2.2-4 2.2V9.8z" fill="currentColor"/>`);
  const iconMap = mediaIcon(`<path d="M4 7l5-2 6 2 5-2v12l-5 2-6-2-5 2V7z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M9 5v12M15 7v12" stroke="currentColor" stroke-width="1.4"/>`);
  const iconForm = mediaIcon(`<rect x="4" y="5" width="16" height="14" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M7 9h10M7 13h7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`);
  const iconInput = mediaIcon(`<rect x="4" y="8" width="16" height="8" rx="1.5" stroke="currentColor" stroke-width="1.7"/>`);
  const iconTextarea = mediaIcon(`<rect x="4" y="6" width="16" height="12" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M8 10h8M8 13h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`);
  const iconSelect = mediaIcon(`<rect x="4" y="8" width="16" height="8" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M14.5 11l1.5 2 1.5-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`);
  const iconButton = mediaIcon(`<rect x="5" y="8" width="14" height="8" rx="3" stroke="currentColor" stroke-width="1.7"/>`);
  const iconCheckbox = mediaIcon(`<rect x="5" y="5" width="14" height="14" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M8.5 12l2.2 2.2L15.5 9.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`);
  const iconRadio = mediaIcon(`<circle cx="12" cy="12" r="7" stroke="currentColor" stroke-width="1.7"/><circle cx="12" cy="12" r="2.7" fill="currentColor"/>`);
  const iconNavbar = mediaIcon(`<rect x="4" y="6" width="16" height="12" rx="1.6" stroke="currentColor" stroke-width="1.7"/><path d="M7 10h3M12 10h2M16 10h1.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`);
  const iconData = mediaIcon(`<circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.5"/><circle cx="16" cy="8" r="2" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="16" r="2" stroke="currentColor" stroke-width="1.5"/><path d="M9.7 9.4l1.8 4.2M14.3 9.4l-1.8 4.2" stroke="currentColor" stroke-width="1.4"/>`);
  const iconTable = mediaIcon(`<rect x="4" y="5" width="16" height="14" rx="1.5" stroke="currentColor" stroke-width="1.7"/><path d="M4 10h16M10 5v14M15 5v14" stroke="currentColor" stroke-width="1.4"/>`);
  const mediaByLabel: Record<string, string> = {
    '1 Column': icon1Col,
    '2 Columns': icon2Col,
    '3 Columns': icon3Col,
    '2 Columns 3/7': icon2Col,
    '2 Columns 50/50': icon2Col,
    '2 Columns 25/75': icon2Col,
    '2 Columns 75/25': icon2Col,
    '3 Columns 50/25/25': icon3Col,
    '3 Columns 25/50/25': icon3Col,
    '3 Columns 25/25/50': icon3Col,
    '4 Columns': icon3Col,
    '5 Columns': icon3Col,
    'Section': iconSection,
    'Divider': iconDivider,
    'Heading': iconHeading,
    'Text': iconText,
    'Link': iconLink,
    'Link Box': iconLink,
    'Image': iconImage,
    'Image Box': iconImage,
    'Video': iconVideo,
    'Map': iconMap,
    'Icon': iconRadio,
    'Form': iconForm,
    'Input': iconInput,
    'Textarea': iconTextarea,
    'Select': iconSelect,
    'Button': iconButton,
    'Label': iconText,
    'Checkbox': iconCheckbox,
    'Radio': iconRadio,
    'Navbar': iconNavbar,
    'Variable': iconData,
    'Condition': iconData,
    'Collection': iconData,
    'Tabs': iconSection,
    'Tooltip': iconText,
    'Accordion': iconSection,
    'Swiper (Slider)': iconMap,
    'List': iconSection,
    'List Pages': iconSection,
    'Table': iconTable,
  };
  const allBlocks = (editor.Blocks.getAll?.() as unknown as any[]) || [];
  allBlocks.forEach((block) => {
    const label = String(block?.get?.('label') || '').trim();
    const media = block?.get?.('media');
    if (!media && label) {
      block.set('media', mediaByLabel[label] || iconTable);
    }
  });
}

const GJS_CSS = `
  /* Fix canvas offset — hide GrapesJS side-panel placeholders, force canvas to fill container */
  .gjs-pn-panels { display: none !important; }
  .gjs-pn-views-container { display: none !important; }
  .gjs-editor-cont { position: relative !important; overflow: hidden !important; flex: 1 1 0% !important; min-width: 0 !important; }
  .gjs-cv-canvas { top: 0 !important; right: 0 !important; bottom: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; }
  .gjs-cv-canvas__frames { width: 100% !important; height: 100% !important; }
  iframe.gjs-frame { width: 100% !important; height: 100% !important; }

  /* ── Nuclear reset: everything inside our panel containers → dark text, white bg ──
     Using :where() keeps specificity at (1,0,0) from the ID so downstream rules can still
     easily override, but the !important ensures we beat grapes.min.css */
  #gjs-blocks-panel :where(*),
  #gjs-layers-panel :where(*),
  #gjs-styles-panel :where(*),
  #gjs-traits-panel :where(*),
  #gjs-assets-panel :where(*),
  #gjs-global-panel :where(*) {
    color: hsl(215, 25%, 27%) !important;
  }

  /* Also catch elements that GrapesJS uses .gjs-one-bg / .gjs-two-bg on (dark backgrounds) */
  .gjs-one-bg, .gjs-two-bg, .gjs-three-bg,
  .gjs-editor .gjs-one-bg, .gjs-editor .gjs-two-bg, .gjs-editor .gjs-three-bg {
    background-color: hsl(0, 0%, 100%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-color: hsl(215, 20%, 89%) !important;
  }

  /* Panel containers: white card background */
  #gjs-blocks-panel,
  #gjs-layers-panel,
  #gjs-styles-panel,
  #gjs-traits-panel,
  #gjs-assets-panel,
  #gjs-global-panel {
    background-color: hsl(0, 0%, 100%) !important;
    color: hsl(215, 25%, 27%) !important;
    font-family: inherit !important;
    font-size: 12px !important;
  }

  /* ════════════════════
     BLOCKS PANEL
     ════════════════════ */
  #gjs-blocks-panel .gjs-block-categories,
  #gjs-blocks-panel .gjs-block-category { background-color: hsl(0, 0%, 100%) !important; }

  #gjs-blocks-panel .gjs-title,
  #gjs-blocks-panel .gjs-block-category__title {
    background-color: hsl(215, 13%, 95%) !important;
    color: hsl(215, 25%, 27%) !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    letter-spacing: 0.06em !important;
    text-transform: uppercase !important;
    padding: 6px 10px !important;
    border-bottom: 1px solid hsl(215, 20%, 89%) !important;
    cursor: pointer !important;
  }

  #gjs-blocks-panel .gjs-blocks-c,
  #gjs-blocks-panel .gjs-block-category .gjs-blocks-c {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    align-content: start !important;
    padding: 6px !important;
    gap: 5px !important;
  }

  #gjs-blocks-panel .gjs-block {
    background-color: hsl(220, 13%, 98%) !important;
    border: 1px solid hsl(215, 20%, 89%) !important;
    border-radius: 8px !important;
    color: hsl(215, 25%, 27%) !important;
    min-height: 56px !important;
    padding: 7px 3px 5px !important;
    cursor: grab !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    transition: background-color 0.12s, border-color 0.12s !important;
  }
  #gjs-blocks-panel .gjs-block:hover {
    background-color: hsl(215, 100%, 97%) !important;
    border-color: hsl(215, 100%, 60%) !important;
  }
  #gjs-blocks-panel .gjs-block.gjs-selected { background-color: hsl(215, 100%, 93%) !important; }

  #gjs-blocks-panel .gjs-block__media,
  #gjs-blocks-panel .gjs-block-media {
    color: hsl(215, 25%, 40%) !important;
    fill: hsl(215, 25%, 40%) !important;
    margin-bottom: 4px !important;
  }
  #gjs-blocks-panel .gjs-block__media svg,
  #gjs-blocks-panel .gjs-block-media svg {
    width: 20px !important; height: 20px !important;
    display: block !important; margin: 0 auto !important;
    color: hsl(215, 25%, 40%) !important;
    fill: hsl(215, 25%, 40%) !important;
  }
  #gjs-blocks-panel .gjs-block-label,
  #gjs-blocks-panel .gjs-block__label {
    font-size: 9px !important;
    font-weight: 600 !important;
    line-height: 1.2 !important;
    color: hsl(215, 25%, 27%) !important;
    text-align: center !important;
  }

  /* ════════════════════
     LAYERS PANEL
     ════════════════════ */
  #gjs-layers-panel .gjs-layers { background-color: hsl(0, 0%, 100%) !important; }

  #gjs-layers-panel .gjs-layer {
    background-color: hsl(0, 0%, 100%) !important;
    border-bottom: 1px solid hsl(220, 13%, 95%) !important;
  }
  #gjs-layers-panel .gjs-layer-title,
  #gjs-layers-panel .gjs-layer-title-c { background-color: transparent !important; }

  #gjs-layers-panel .gjs-layer-name,
  #gjs-layers-panel .gjs-layer-caret,
  #gjs-layers-panel .gjs-layer-vis,
  #gjs-layers-panel .gjs-layer-move,
  #gjs-layers-panel .gjs-layer-count {
    color: hsl(215, 25%, 27%) !important;
    fill: hsl(215, 25%, 27%) !important;
    opacity: 1 !important;
  }
  #gjs-layers-panel .gjs-layer-title:hover { background-color: hsl(215, 100%, 97%) !important; }
  #gjs-layers-panel .gjs-layer-title:hover * { color: hsl(215, 100%, 35%) !important; fill: hsl(215, 100%, 35%) !important; }

  #gjs-layers-panel .gjs-layer.gjs-selected > .gjs-layer-title-c > .gjs-layer-title {
    background-color: hsl(215, 100%, 93%) !important;
  }
  #gjs-layers-panel .gjs-layer.gjs-selected > .gjs-layer-title-c > .gjs-layer-title * {
    color: hsl(215, 100%, 30%) !important;
    fill: hsl(215, 100%, 30%) !important;
    font-weight: 600 !important;
  }
  #gjs-layers-panel .gjs-layer-children { border-left: 2px solid hsl(215, 20%, 89%) !important; }

  /* ════════════════════
     STYLES PANEL
     ════════════════════ */
  #gjs-styles-panel .gjs-sm-sector { border-bottom: 1px solid hsl(215, 20%, 89%) !important; }

  #gjs-styles-panel .gjs-sm-sector-title,
  #gjs-styles-panel .gjs-sm-sector__title {
    background-color: hsl(215, 13%, 95%) !important;
    color: hsl(215, 25%, 27%) !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    padding: 8px 10px !important;
    cursor: pointer !important;
    user-select: none !important;
    border-bottom: 1px solid hsl(215, 20%, 89%) !important;
  }
  #gjs-styles-panel .gjs-sm-sector-title:hover,
  #gjs-styles-panel .gjs-sm-sector__title:hover { background-color: hsl(215, 20%, 92%) !important; }

  #gjs-styles-panel .gjs-sm-properties { padding: 8px 10px !important; }

  #gjs-styles-panel .gjs-sm-label,
  #gjs-styles-panel .gjs-sm-property label,
  #gjs-styles-panel .gjs-field label {
    color: hsl(215, 16%, 40%) !important;
    font-size: 10px !important;
    font-weight: 500 !important;
  }

  #gjs-styles-panel .gjs-field,
  #gjs-styles-panel input[type="text"],
  #gjs-styles-panel input[type="number"],
  #gjs-styles-panel select,
  #gjs-styles-panel textarea {
    background-color: hsl(220, 13%, 98%) !important;
    border: 1px solid hsl(215, 20%, 89%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-radius: 5px !important;
    font-size: 11px !important;
    padding: 3px 6px !important;
  }
  #gjs-styles-panel .gjs-field input,
  #gjs-styles-panel .gjs-field select {
    background-color: transparent !important;
    border: none !important;
    color: hsl(215, 25%, 27%) !important;
    font-size: 11px !important;
  }
  #gjs-styles-panel .gjs-field:focus-within,
  #gjs-styles-panel input:focus,
  #gjs-styles-panel select:focus {
    border-color: hsl(151, 74%, 40%) !important;
    outline: none !important;
    box-shadow: 0 0 0 2px hsl(151, 74%, 40%, 0.2) !important;
  }
  /* Placeholders must be readable — GrapesJS shows computed values as placeholder="0" etc */
  #gjs-styles-panel input::placeholder,
  #gjs-traits-panel input::placeholder {
    color: hsl(215, 25%, 40%) !important;
    opacity: 1 !important;
  }
  #gjs-styles-panel input[type="color"] { cursor: pointer !important; }
  #gjs-styles-panel input[type="range"] { accent-color: hsl(215, 100%, 50%) !important; }

  #gjs-styles-panel .gjs-sm-btn,
  #gjs-styles-panel .gjs-sm-btn-active {
    background-color: hsl(215, 13%, 95%) !important;
    border: 1px solid hsl(215, 20%, 89%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-radius: 4px !important;
  }
  #gjs-styles-panel .gjs-sm-btn:hover { background-color: hsl(215, 20%, 90%) !important; }
  #gjs-styles-panel .gjs-sm-btn-active { background-color: hsl(215, 100%, 93%) !important; border-color: hsl(215, 100%, 60%) !important; color: hsl(215, 100%, 30%) !important; }

  #gjs-styles-panel .gjs-four-color,
  #gjs-styles-panel .gjs-four-color-h {
    background-color: hsl(215, 13%, 95%) !important;
    border: 1px solid hsl(215, 20%, 89%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-radius: 4px !important;
  }
  #gjs-styles-panel .gjs-sm-composite-note { color: hsl(215, 16%, 50%) !important; font-size: 10px !important; }

  /* Compact composite boxes (margin / padding / border) */
  #gjs-styles-panel .gjs-sm-composite .gjs-sm-properties,
  #gjs-styles-panel .gjs-sm-property--composite .gjs-sm-properties {
    background-color: hsl(215, 13%, 95%) !important;
    border-radius: 6px !important;
    padding: 6px !important;
    gap: 4px !important;
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
  }
  #gjs-styles-panel .gjs-sm-composite .gjs-sm-property,
  #gjs-styles-panel .gjs-sm-property--composite .gjs-sm-property {
    margin: 0 !important;
    padding: 2px 0 !important;
  }
  #gjs-styles-panel .gjs-sm-composite .gjs-sm-label,
  #gjs-styles-panel .gjs-sm-property--composite .gjs-sm-label {
    font-size: 9px !important;
    margin-bottom: 2px !important;
    color: hsl(215, 16%, 45%) !important;
  }
  #gjs-styles-panel .gjs-sm-composite .gjs-field,
  #gjs-styles-panel .gjs-sm-property--composite .gjs-field {
    background-color: hsl(0, 0%, 100%) !important;
    border: 1px solid hsl(215, 20%, 85%) !important;
    padding: 2px 4px !important;
  }
  #gjs-styles-panel .gjs-sm-composite .gjs-field input,
  #gjs-styles-panel .gjs-sm-property--composite .gjs-field input {
    color: hsl(215, 25%, 27%) !important;
    font-size: 11px !important;
  }
  #gjs-styles-panel .gjs-sm-composite .gjs-field input::placeholder,
  #gjs-styles-panel .gjs-sm-property--composite .gjs-field input::placeholder {
    color: hsl(215, 20%, 50%) !important;
    opacity: 1 !important;
  }
  #gjs-styles-panel .gjs-sm-composite .gjs-field select,
  #gjs-styles-panel .gjs-sm-property--composite .gjs-field select {
    color: hsl(215, 16%, 45%) !important;
    font-size: 9px !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
  }

  /* ════════════════════
     TRAITS (PROPERTIES) PANEL
     ════════════════════ */
  #gjs-traits-panel .gjs-trt-traits { padding: 8px 10px !important; }

  #gjs-traits-panel .gjs-trt-trait,
  #gjs-traits-panel .gjs-trait { border-bottom: 1px solid hsl(220, 13%, 95%) !important; padding: 6px 0 !important; }

  #gjs-traits-panel .gjs-label,
  #gjs-traits-panel .gjs-trt-trait__label,
  #gjs-traits-panel label {
    color: hsl(215, 16%, 40%) !important;
    font-size: 10px !important;
    font-weight: 500 !important;
  }

  #gjs-traits-panel .gjs-field,
  #gjs-traits-panel input[type="text"],
  #gjs-traits-panel input[type="number"],
  #gjs-traits-panel input[type="url"],
  #gjs-traits-panel input[type="email"],
  #gjs-traits-panel select,
  #gjs-traits-panel textarea {
    background-color: hsl(220, 13%, 98%) !important;
    border: 1px solid hsl(215, 20%, 89%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-radius: 5px !important;
    font-size: 11px !important;
    padding: 3px 6px !important;
  }
  #gjs-traits-panel .gjs-field input,
  #gjs-traits-panel .gjs-field select {
    background-color: transparent !important;
    border: none !important;
    color: hsl(215, 25%, 27%) !important;
  }
  #gjs-traits-panel input:focus,
  #gjs-traits-panel select:focus,
  #gjs-traits-panel textarea:focus {
    border-color: hsl(151, 74%, 40%) !important;
    outline: none !important;
    box-shadow: 0 0 0 2px hsl(151, 74%, 40%, 0.2) !important;
  }
  #gjs-traits-panel input[type="checkbox"] { accent-color: hsl(215, 100%, 50%) !important; }

  #gjs-traits-panel .gjs-four-color,
  #gjs-traits-panel .gjs-four-color-h {
    background-color: hsl(215, 13%, 95%) !important;
    border: 1px solid hsl(215, 20%, 89%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-radius: 4px !important;
  }

  /* ════════════════════
     ASSETS PANEL
     ════════════════════ */
  #gjs-assets-panel .gjs-am-assets-cont { padding: 8px !important; }
  #gjs-assets-panel .gjs-am-asset {
    background-color: hsl(220, 13%, 98%) !important;
    border: 1px solid hsl(215, 20%, 89%) !important;
    border-radius: 6px !important;
    color: hsl(215, 25%, 27%) !important;
  }
  #gjs-assets-panel .gjs-am-asset:hover { border-color: hsl(215, 100%, 60%) !important; }

  /* ════════════════════
     GLOBAL / COLOR-STYLES PANEL
     ════════════════════ */
  #gjs-global-panel input[type="color"] { cursor: pointer !important; border-radius: 3px !important; }

  /* ════════════════════
     COLOR PICKER OVERLAY (floating, outside panel IDs)
     ════════════════════ */
  .gjs-mdl-dialog,
  .gjs-mdl-content,
  .sp-container,
  .sp-picker-container,
  .sp-palette-container {
    background-color: hsl(0, 0%, 100%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-color: hsl(215, 20%, 89%) !important;
  }
  .sp-input-container input,
  .sp-dragger,
  .sp-hue { border-color: hsl(215, 20%, 89%) !important; }
  .sp-cancel, .sp-choose {
    background-color: hsl(215, 13%, 95%) !important;
    color: hsl(215, 25%, 27%) !important;
    border-color: hsl(215, 20%, 89%) !important;
  }
`;


type LeftPanelId = 'blocks' | 'global' | 'pages' | 'layers' | 'assets' | 'ai';
type AiMessage = { role: 'user' | 'assistant'; content: string; html?: string; attachment?: string; isHtml?: boolean };
type RightPanelId = 'styles' | 'traits';
type ColorStyleKey =
  | 'primaryColor'
  | 'secondaryColor'
  | 'accentColor'
  | 'successColor'
  | 'warningColor'
  | 'errorColor'
  | 'bodyBackground'
  | 'bodyColor'
  | 'headingColor'
  | 'subheadingColor'
  | 'buttonBackground'
  | 'buttonColor'
  | 'linkColor'
  | 'borderColor';
type GlobalStyles = {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  bodyBackground: string;
  bodyColor: string;
  bodyFontSize: number;
  bodyLineHeight: number;
  bodyFontFamily: string;
  headingColor: string;
  headingFontSize: number;
  headingLineHeight: number;
  headingFontFamily: string;
  subheadingColor: string;
  subheadingFontSize: number;
  subheadingLineHeight: number;
  subheadingFontFamily: string;
  buttonBackground: string;
  buttonColor: string;
  buttonRadius: number;
  linkColor: string;
  linkDecoration: 'none' | 'underline';
  borderRadius: number;
  borderColor: string;
};

// ─── datastorePlugin ──────────────────────────────────────────────────────────
//
//  Repeater / Collection pattern:
//    1. Drag "Data Collection" onto canvas  → droppable container
//    2. Drop any blocks inside to design ONE card template
//    3. Type {{fieldName}} in text content or image src to bind fields
//    4. Set "Store ID" (DataStore) or "API URL" (any endpoint) → data loads
//
//  Why the placeholder uses CSS ::before instead of innerHTML:
//    Writing innerHTML in design mode would destroy GrapesJS's child component
//    tracking and prevent blocks from being dropped in. CSS ::before is purely
//    visual and never touches the DOM children.
//
// ─────────────────────────────────────────────────────────────────────────────
function datastorePlugin(editor: any) {

  // ── Inject canvas CSS for the empty-state placeholder ──────────────────────
  editor.on('load', () => {
    try {
      const doc = editor.Canvas.getDocument();
      const s = doc.createElement('style');
      s.setAttribute('id', 'wb-ds-styles');
      s.textContent = `
        .wb-ds-collection { box-sizing: border-box; }
        .wb-ds-empty {
          min-height: 160px;
          border: 2px dashed #cbd5e1 !important;
          border-radius: 12px !important;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .wb-ds-empty::before {
          content: "📦  Drop blocks inside to design your card. Type {{fieldName}} in text / image src. Then set Store ID or API URL.";
          font-family: sans-serif;
          font-size: 12px;
          color: #94a3b8;
          text-align: center;
          padding: 0 24px;
          pointer-events: none;
        }
      `;
      doc.head.appendChild(s);
    } catch (_) { /* canvas not ready — noop */ }
  });

  editor.Components.addType('wb-ds-collection', {
    model: {
      defaults: {
        tagName: 'div',
        droppable: true,
        classes: ['wb-ds-collection', 'wb-ds-empty'],
        // ── data source ─────────────────────────────────────────────────────
        sourceType:  'datastore',   // 'datastore' | 'api'
        storeId:     '',
        apiUrl:      '',
        apiHeaders:  '',            // optional JSON object string
        // ── display ─────────────────────────────────────────────────────────
        limit:       12,
        columns:     'auto-fill',
        accentColor: '#1ab169',
        'script-props': ['sourceType', 'storeId', 'apiUrl', 'apiHeaders', 'limit', 'columns', 'accentColor'],
        traits: [
          {
            type: 'select', name: 'sourceType', label: '📡 Source type', changeProp: true,
            options: [
              { id: 'datastore', name: '📦 DataStore (by ID)' },
              { id: 'api',       name: '🌐 Custom API URL' },
            ],
          },
          {
            type: 'text', name: 'storeId', label: 'Store ID',
            placeholder: 'Paste DataStore UUID', changeProp: true,
          },
          {
            type: 'text', name: 'apiUrl', label: 'API URL',
            placeholder: 'https://api.example.com/items', changeProp: true,
          },
          {
            type: 'text', name: 'apiHeaders', label: 'Extra headers (JSON)',
            placeholder: '{"Authorization":"Bearer token"}', changeProp: true,
          },
          {
            type: 'number', name: 'limit', label: 'Limit (records)',
            placeholder: '12', changeProp: true,
          },
          {
            type: 'select', name: 'columns', label: 'Grid columns', changeProp: true,
            options: [
              { id: 'auto-fill', name: 'Auto (responsive)' },
              { id: '1',         name: '1 column' },
              { id: '2',         name: '2 columns' },
              { id: '3',         name: '3 columns' },
              { id: '4',         name: '4 columns' },
            ],
          },
          {
            type: 'text', name: 'accentColor', label: 'Accent color',
            placeholder: '#1ab169', changeProp: true,
          },
        ],

        // Script runs in the canvas iframe — no React, only fetch + DOM.
        // CRITICAL: in design mode (no source configured) this script NEVER
        // touches innerHTML so GrapesJS can manage dropped children freely.
        // The empty-state visual is handled by the CSS ::before above.
        script(props: any) {
          const el = this as unknown as HTMLElement & { __wbTpl?: string };

          const esc = (v: any) => {
            const s = v == null ? '' : String(v);
            return s
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;');
          };

          const isDatastore = !props.sourceType || props.sourceType === 'datastore';
          const hasSource   = isDatastore ? !!props.storeId?.trim() : !!props.apiUrl?.trim();

          // ── DESIGN MODE: no source set ──────────────────────────────────────
          if (!hasSource) {
            // If we previously replaced innerHTML with live data, restore template
            if (el.__wbTpl !== undefined) {
              el.innerHTML = el.__wbTpl;
              el.__wbTpl   = undefined;
            }
            // ← NEVER write innerHTML here — GrapesJS manages children
            // The CSS .wb-ds-empty::before shows the hint when el is childless
            return;
          }

          // ── LIVE MODE: snapshot template, fetch, substitute ─────────────────
          if (el.__wbTpl === undefined) {
            // Remove the CSS empty-state class (children will be data cards now)
            el.classList.remove('wb-ds-empty');
            el.__wbTpl = el.innerHTML;
          }
          const templateHTML = el.__wbTpl;
          const accent = props.accentColor || '#1ab169';

          // Loading spinner (replaces template temporarily)
          el.innerHTML = `
            <style>@keyframes _wbspin{to{transform:rotate(360deg)}}</style>
            <div style="display:flex;align-items:center;justify-content:center;min-height:120px;
              gap:10px;color:#64748b;font-family:sans-serif;font-size:13px;">
              <div style="width:18px;height:18px;border:2px solid #e2e8f0;
                border-top-color:${accent};border-radius:50%;
                animation:_wbspin .8s linear infinite;"></div>
              Loading…
            </div>`;

          // ── Build fetch URL + headers ───────────────────────────────────────
          let fetchUrl = '';
          const headers: Record<string, string> = {};

          if (isDatastore) {
            const token: string | null = localStorage.getItem('auth_token');
            fetchUrl = `/api/v1/data-stores/${props.storeId.trim()}/records?limit=${props.limit || 12}`;
            if (token) headers['Authorization'] = `Bearer ${token}`;
          } else {
            fetchUrl = props.apiUrl.trim();
            if (props.apiHeaders?.trim()) {
              try { Object.assign(headers, JSON.parse(props.apiHeaders)); } catch (_) {}
            }
          }

          fetch(fetchUrl, { headers })
            .then((r: Response) => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              return r.json();
            })
            .then((json: any) => {
              let rows: any[] = [];
              if      (Array.isArray(json))                  rows = json;
              else if (Array.isArray(json?.data))            rows = json.data;
              else if (Array.isArray(json?.data?.data))      rows = json.data.data;
              else if (Array.isArray(json?.records))         rows = json.records;
              else if (Array.isArray(json?.results))         rows = json.results;
              else if (Array.isArray(json?.items))           rows = json.items;

              if (!rows.length) {
                el.innerHTML = `
                  <div style="display:flex;align-items:center;justify-content:center;
                    min-height:80px;border:1px dashed #e2e8f0;border-radius:10px;
                    color:#94a3b8;font-family:sans-serif;font-size:13px;">
                    No records found.
                  </div>`;
                return;
              }

              // Clone template × records, substituting {{fieldName}} tokens
              const cards = rows
                .map((row: any) =>
                  templateHTML.replace(/\{\{(\w+)\}\}/g, (_: string, key: string) => {
                    const v = row[key];
                    return v == null ? '' : esc(String(v));
                  })
                )
                .join('');

              const gridCols =
                props.columns === 'auto-fill'
                  ? 'repeat(auto-fill,minmax(220px,1fr))'
                  : `repeat(${props.columns},1fr)`;

              el.innerHTML = `
                <div style="display:grid;grid-template-columns:${gridCols};gap:16px;">
                  ${cards}
                </div>`;
            })
            .catch((err: Error) => {
              el.innerHTML = `
                <div style="display:flex;align-items:center;gap:10px;padding:14px 16px;
                  border-radius:8px;background:#fef2f2;border:1px solid #fca5a5;
                  font-family:sans-serif;">
                  <span>⚠️</span>
                  <div>
                    <div style="font-size:13px;font-weight:600;color:#dc2626;">Failed to load</div>
                    <div style="font-size:11px;color:#ef4444;">${err.message}</div>
                  </div>
                </div>`;
            });
        },
      },
    },

    // ── View: keep the wb-ds-empty class in sync with children ─────────────
    view: {
      onRender({ el, model }: any) {
        const syncEmpty = () => {
          const isDatastore = !model.get('sourceType') || model.get('sourceType') === 'datastore';
          const hasSource   = isDatastore ? !!model.get('storeId') : !!model.get('apiUrl');
          if (!hasSource && model.components().length === 0) {
            el.classList.add('wb-ds-empty');
          } else {
            el.classList.remove('wb-ds-empty');
          }
        };
        syncEmpty();
        model.listenTo(model.components(), 'add remove reset', syncEmpty);
        model.on('change:storeId change:apiUrl change:sourceType', syncEmpty);
      },
    },
  });

  // ── Hint trait for blocks dropped inside the collection ───────────────────
  editor.on('component:select', (component: any) => {
    let p = component.parent?.();
    let inside = false;
    while (p) {
      if (p.get('type') === 'wb-ds-collection') { inside = true; break; }
      p = p.parent?.();
    }
    if (!inside) return;
    if (component.getTrait('wb-bind-hint')) return;
    component.addTrait(
      {
        type: 'text', id: 'wb-bind-hint', name: 'wb-bind-hint',
        label: '💡 Bind field',
        placeholder: 'Type {{fieldName}} in content / src',
      },
      { at: 0 },
    );
  });

  editor.Blocks.add('wb-ds-collection', {
    label: 'Data Collection',
    category: 'Data Sources',
    media: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="24" height="24">
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M3 5v4c0 1.657 4.03 3 9 3s9-1.343 9-3V5"/>
      <path d="M3 9v4c0 1.657 4.03 3 9 3s9-1.343 9-3V9"/>
      <path d="M3 13v4c0 1.657 4.03 3 9 3s9-1.343 9-3v-4"/>
    </svg>`,
    content: { type: 'wb-ds-collection' },
  });
}

export default function WebsiteBuilderEditor() {
  const { siteId, pageId } = useParams<{ siteId: string; pageId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const containerRef = useRef<HTMLDivElement>(null);
  // @REMOVED grapesjs: previously typed as Editor (from 'grapesjs')
  const editorRef = useRef<any>(null);

  const [site, setSite] = useState<WbSite | null>(null);
  const [page, setPage] = useState<WbPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageMode, setPageMode] = useState<'static' | 'ai-code'>('static');
  const [saving, setSaving] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [activePanel, setActivePanel] = useState<LeftPanelId>('blocks');
  const [activeRightPanel, setActiveRightPanel] = useState<RightPanelId>('styles');
  const [device, setDevice] = useState('Desktop');
  const [pages, setPages] = useState<Array<{ id: string; name: string }>>([]);
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // "+ New page from template" modal — opens via the secondary button in the
  // Pages panel. On a successful instantiate the gallery hands back the new
  // page id and we navigate the editor's URL to it.
  const [pageTemplateModalOpen, setPageTemplateModalOpen] = useState(false);
  const [canvasIsEmpty, setCanvasIsEmpty] = useState(true);
  const [blockSearch, setBlockSearch] = useState('');
  const [aiMessages, setAiMessages] = useState<AiMessage[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLiveHtml, setAiLiveHtml] = useState('');
  const [aiAttachment, setAiAttachment] = useState<{ name: string; dataUrl: string } | null>(null);
  const [isListening, setIsListening] = useState(false);
  const aiMessagesEndRef = useRef<HTMLDivElement>(null);
  const aiFileRef = useRef<HTMLInputElement>(null);
  const speechRef = useRef<any>(null);
  const [globalStyles, setGlobalStyles] = useState<GlobalStyles>({
    primaryColor: '#111827',
    secondaryColor: '#6b7280',
    accentColor: '#c08457',
    successColor: '#166534',
    warningColor: '#b45309',
    errorColor: '#b91c1c',
    bodyBackground: '#fcfaf7',
    bodyColor: '#111827',
    bodyFontSize: 16,
    bodyLineHeight: 1.6,
    bodyFontFamily: 'DM Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    headingColor: '#111827',
    headingFontSize: 60,
    headingLineHeight: 1.05,
    headingFontFamily: 'DM Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    subheadingColor: '#111827',
    subheadingFontSize: 38,
    subheadingLineHeight: 1.15,
    subheadingFontFamily: 'DM Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    buttonBackground: '#111827',
    buttonColor: '#ffffff',
    buttonRadius: 9999,
    linkColor: '#1ab169',
    linkDecoration: 'none',
    borderRadius: 12,
    borderColor: '#e5e7eb',
  });
  const [colorInputs, setColorInputs] = useState<Record<ColorStyleKey, string>>({
    primaryColor: '#111827',
    secondaryColor: '#6b7280',
    accentColor: '#c08457',
    successColor: '#166534',
    warningColor: '#b45309',
    errorColor: '#b91c1c',
    bodyBackground: '#fcfaf7',
    bodyColor: '#111827',
    headingColor: '#111827',
    subheadingColor: '#111827',
    buttonBackground: '#111827',
    buttonColor: '#ffffff',
    linkColor: '#1ab169',
    borderColor: '#e5e7eb',
  });

  useEffect(() => {
    setColorInputs((prev) => ({
      ...prev,
      primaryColor: globalStyles.primaryColor,
      secondaryColor: globalStyles.secondaryColor,
      accentColor: globalStyles.accentColor,
      successColor: globalStyles.successColor,
      warningColor: globalStyles.warningColor,
      errorColor: globalStyles.errorColor,
      bodyBackground: globalStyles.bodyBackground,
      bodyColor: globalStyles.bodyColor,
      headingColor: globalStyles.headingColor,
      subheadingColor: globalStyles.subheadingColor,
      buttonBackground: globalStyles.buttonBackground,
      buttonColor: globalStyles.buttonColor,
      linkColor: globalStyles.linkColor,
      borderColor: globalStyles.borderColor,
    }));
  }, [globalStyles]);

  useEffect(() => {
    const panel = document.getElementById('gjs-blocks-panel');
    if (!panel) return;
    const q = blockSearch.toLowerCase().trim();
    panel.querySelectorAll<HTMLElement>('.gjs-block').forEach((block) => {
      const label = (block.querySelector('.gjs-block-label, .gjs-block__label')?.textContent ?? '').toLowerCase();
      block.style.display = !q || label.includes(q) ? '' : 'none';
    });
    panel.querySelectorAll<HTMLElement>('.gjs-block-category').forEach((cat) => {
      const anyVisible = Array.from(cat.querySelectorAll<HTMLElement>('.gjs-block')).some((b) => b.style.display !== 'none');
      cat.style.display = anyVisible ? '' : 'none';
    });
  }, [blockSearch]);

  useEffect(() => {
    if (!siteId || !pageId) return;
    (async () => {
      const [siteRes, pageRes] = await Promise.all([
        wbSitesApi.get(siteId),
        wbPagesApi.get(siteId, pageId),
      ]);
      if (!siteRes.error) setSite((siteRes.data as any)?.data ?? siteRes.data);
      if (!pageRes.error) {
        const p = (pageRes.data as any)?.data ?? pageRes.data;
        setPage(p);
        if (p?.mode === 'ai-code') setPageMode('ai-code');
      }
      setLoading(false);
    })();
  }, [siteId, pageId]);

  useEffect(() => {
    if (!containerRef.current) return;

    // ── [grapesjs REMOVED] Visual editor was initialized here via:
    //     const editor = grapesjs.init({ ... });
    //     Plugins: ecommercePlugin, basicLayoutBlocksPlugin, datastorePlugin
    //     All grapesjs-related setup removed 2026-06-18.
    editorRef.current = null;
    setEditorReady(false);
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !editorReady || !page) return;
    if (page.content && Object.keys(page.content).length > 0) {
      editor.loadProjectData(page.content as any);
      setCanvasIsEmpty(false);
      readGlobalStylesFromCanvas();
    } else {
      applyGlobalStyles(globalStyles);
    }
    // Fix page name: GrapesJS assigns a random ID as the default page name.
    // Always rename it to match our DB page title so the Pages panel shows "Home" not "MKrONHEbYrQIF3tJ".
    const pagesApi = (editor as any).Pages;
    if (pagesApi && page.title) {
      const allPages: any[] = pagesApi.getAll?.() || [];
      if (allPages.length > 0) {
        allPages[0].set('name', page.title);
      }
      const list = allPages.map((p: any) => ({
        id: p.getId?.() || p.id,
        name: p.get('name') || page.title || 'Untitled',
      }));
      setPages(list);
      const selected = pagesApi.getSelected?.();
      setActivePageId(selected?.getId?.() || selected?.id || null);
    }
  }, [page, editorReady]);

  function switchDevice(d: string) {
    setDevice(d);
    editorRef.current?.setDevice(d);
  }

  function handleAddPage() {
    const editor = editorRef.current as any;
    if (!editor?.Pages) return;
    const newPage = editor.Pages.add({
      id: `page-${Date.now()}`,
      name: `Page ${pages.length + 1}`,
    });
    editor.Pages.select(newPage);
  }

  function handleSelectPage(id: string) {
    const editor = editorRef.current as any;
    if (!editor?.Pages) return;
    editor.Pages.select(id);
  }

  function handleDeletePage(id: string, name: string) {
    const editor = editorRef.current as any;
    if (!editor?.Pages) return;

    if (pages.length <= 1) {
      toast({
        title: 'Cannot delete page',
        description: 'At least one page is required.',
        variant: 'destructive',
      });
      return;
    }

    const confirmed = window.confirm(`Delete "${name}" page? This cannot be undone.`);
    if (!confirmed) return;

    const pageToDelete = editor.Pages.get(id);
    if (!pageToDelete) return;

    editor.Pages.remove(pageToDelete);
    toast({ variant: "success",
      title: 'Page deleted',
      description: `"${name}" was removed from this project.`,
    });
  }

  function handleTogglePreview() {
    const editor = editorRef.current as any;
    if (!editor?.Commands) return;
    const commandId = 'core:preview';
    const isActive = editor.Commands.isActive?.(commandId);
    if (isActive) {
      editor.Commands.stop(commandId);
      setIsPreviewMode(false);
      return;
    }
    editor.Commands.run(commandId);
    setIsPreviewMode(true);
  }

  function readGlobalStylesFromCanvas() {
    const editor = editorRef.current as any;
    if (!editor?.Css) return;

    const bodyRule = editor.Css.getRule?.('body');
    const headingRule = editor.Css.getRule?.('h1, h2, h3, h4, h5, h6');
    const subheadingRule = editor.Css.getRule?.('h2, h3');
    const buttonRule = editor.Css.getRule?.('button, .btn, [role="button"]');
    const linkRule = editor.Css.getRule?.('a');
    const borderRule = editor.Css.getRule?.('input, textarea, select, button, .card, [class*="card"]');
    const bodyStyles = bodyRule?.getStyle?.() || {};
    const headingStyles = headingRule?.getStyle?.() || {};
    const subheadingStyles = subheadingRule?.getStyle?.() || {};
    const buttonStyles = buttonRule?.getStyle?.() || {};
    const linkStyles = linkRule?.getStyle?.() || {};
    const borderStyles = borderRule?.getStyle?.() || {};

    setGlobalStyles((prev) => ({
      ...prev,
      bodyBackground: bodyStyles['background-color'] || prev.bodyBackground,
      bodyColor: bodyStyles.color || prev.bodyColor,
      headingColor: headingStyles.color || prev.headingColor,
      subheadingColor: subheadingStyles.color || prev.subheadingColor,
      buttonBackground: buttonStyles['background-color'] || prev.buttonBackground,
      buttonColor: buttonStyles.color || prev.buttonColor,
      linkColor: linkStyles.color || prev.linkColor,
      bodyFontFamily: bodyStyles['font-family'] || prev.bodyFontFamily,
      headingFontFamily: headingStyles['font-family'] || prev.headingFontFamily,
      subheadingFontFamily: subheadingStyles['font-family'] || prev.subheadingFontFamily,
      bodyFontSize: parseInt((bodyStyles['font-size'] || `${prev.bodyFontSize}px`).toString(), 10) || prev.bodyFontSize,
      bodyLineHeight: parseFloat((bodyStyles['line-height'] || prev.bodyLineHeight).toString()) || prev.bodyLineHeight,
      headingFontSize: parseInt((headingStyles['font-size'] || `${prev.headingFontSize}px`).toString(), 10) || prev.headingFontSize,
      headingLineHeight: parseFloat((headingStyles['line-height'] || prev.headingLineHeight).toString()) || prev.headingLineHeight,
      subheadingFontSize: parseInt((subheadingStyles['font-size'] || `${prev.subheadingFontSize}px`).toString(), 10) || prev.subheadingFontSize,
      subheadingLineHeight: parseFloat((subheadingStyles['line-height'] || prev.subheadingLineHeight).toString()) || prev.subheadingLineHeight,
      buttonRadius: parseInt((buttonStyles['border-radius'] || `${prev.buttonRadius}px`).toString(), 10) || prev.buttonRadius,
      borderRadius: parseInt((borderStyles['border-radius'] || `${prev.borderRadius}px`).toString(), 10) || prev.borderRadius,
      borderColor: borderStyles['border-color'] || prev.borderColor,
    }));
  }

  function applyGlobalStyles(next: GlobalStyles) {
    const editor = editorRef.current as any;
    if (!editor?.Css) return;

    editor.Css.setRule(':root', {
      '--global-primary': next.primaryColor,
      '--global-secondary': next.secondaryColor,
      '--global-accent': next.accentColor,
      '--global-success': next.successColor,
      '--global-warning': next.warningColor,
      '--global-error': next.errorColor,
      '--global-border-color': next.borderColor,
      '--global-border-radius': `${next.borderRadius}px`,
    });

    editor.Css.setRule('body', {
      'background-color': next.bodyBackground,
      color: next.bodyColor,
      'font-family': next.bodyFontFamily,
      'font-size': `${next.bodyFontSize}px`,
      'line-height': String(next.bodyLineHeight),
    });

    editor.Css.setRule('h1', {
      color: next.headingColor,
      'font-family': next.headingFontFamily,
      'font-size': `${next.headingFontSize}px`,
      'line-height': String(next.headingLineHeight),
    });
    editor.Css.setRule('h2, h3', {
      color: next.subheadingColor,
      'font-family': next.subheadingFontFamily,
      'font-size': `${next.subheadingFontSize}px`,
      'line-height': String(next.subheadingLineHeight),
    });

    editor.Css.setRule('button, .btn, [role="button"]', {
      'background-color': next.buttonBackground,
      color: next.buttonColor,
      'border-radius': `${next.buttonRadius}px`,
    });

    editor.Css.setRule('a', {
      color: next.linkColor,
      'text-decoration': next.linkDecoration,
    });

    editor.Css.setRule('input, textarea, select, button, .card, [class*="card"]', {
      'border-color': next.borderColor,
      'border-radius': `${next.borderRadius}px`,
    });

    editor.Css.setRule('button, input, textarea, select, p, li, label', {
      'font-family': next.bodyFontFamily,
    });
    editor.trigger('change');
  }

  function updateGlobalStyles<K extends keyof GlobalStyles>(key: K, value: GlobalStyles[K]) {
    setGlobalStyles((prev) => {
      const next = { ...prev, [key]: value };
      applyGlobalStyles(next);
      return next;
    });
  }

  function isHexColor(value: string) {
    return /^#([0-9a-fA-F]{6})$/.test(value);
  }

  function renderColorControl(label: string, key: ColorStyleKey) {
    const value = colorInputs[key] || '#000000';
    return (
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs text-muted-foreground">{label}</label>
        <div className="flex items-center gap-1.5 w-[155px] rounded border border-border bg-background px-1.5 py-1">
          <input
            type="color"
            value={isHexColor(value) ? value : '#000000'}
            onChange={(e) => {
              const next = e.target.value;
              setColorInputs((prev) => ({ ...prev, [key]: next }));
              updateGlobalStyles(key as any, next as any);
            }}
            className="h-6 w-7 rounded border border-border bg-background p-0 cursor-pointer"
            title={label}
          />
          <input
            type="text"
            value={value}
            onChange={(e) => {
              const raw = e.target.value;
              if (!/^#?[0-9a-fA-F]{0,6}$/.test(raw)) return;
              const normalized = raw.startsWith('#') ? raw : `#${raw}`;
              setColorInputs((prev) => ({ ...prev, [key]: normalized }));
              if (isHexColor(normalized)) {
                updateGlobalStyles(key as any, normalized as any);
              }
            }}
            onBlur={() => {
              const current = colorInputs[key];
              if (!isHexColor(current)) {
                setColorInputs((prev) => ({ ...prev, [key]: globalStyles[key] as string }));
              }
            }}
            className="h-6 w-full bg-transparent border-0 text-xs text-foreground focus:outline-none"
          />
        </div>
      </div>
    );
  }

  async function handleAiSend() {
    const prompt = aiInput.trim();
    if ((!prompt && !aiAttachment) || aiLoading) return;
    const attachment = aiAttachment;
    setAiInput('');
    setAiAttachment(null);
    const fullPrompt = attachment ? `${prompt}\n\n[Reference image attached: ${attachment.name}]` : prompt;
    const userMsg: AiMessage = { role: 'user', content: prompt, attachment: attachment?.dataUrl };

    // Build history — include all messages but flag html ones so backend can summarize them
    const history = aiMessages
      .filter((m) => m.content)
      .map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.isHtml ? m.content : m.content,
        isHtml: !!m.isHtml,
      }));

    setAiMessages((prev) => [...prev, userMsg, { role: 'assistant', content: '' }]);
    setAiLoading(true);
    setTimeout(() => aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    let accumulated = '';
    setAiLiveHtml('');

    const updateCanvasBody = (html: string) => {
      try {
        const frame = (editorRef.current as any)?.Canvas?.getFrameEl?.();
        const doc = frame?.contentDocument || frame?.contentWindow?.document;
        if (doc?.body) doc.body.innerHTML = html;
      } catch { /* ignore */ }
    };

    await wbAiApi.generateStream(
      fullPrompt,
      history,
      (chunk) => {
        accumulated += chunk;
        setAiMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: accumulated };
          return updated;
        });
        if (accumulated.trimStart().startsWith('<')) {
          updateCanvasBody(accumulated);
          setAiLiveHtml(accumulated);
          setCanvasIsEmpty(false);
        }
        aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      },
      () => {
        setAiLoading(false);
        const isHtml = accumulated.trimStart().startsWith('<');
        setAiLiveHtml('');
        if (isHtml) {
          // Auto-apply: register with GrapesJS so elements become editable
          editorRef.current?.setComponents(accumulated);
          setCanvasIsEmpty(false);
        }
        setAiMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: accumulated,
            html: isHtml ? accumulated : undefined,
            isHtml,
          };
          return updated;
        });
        setTimeout(() => aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      },
      (errMsg) => {
        setAiLoading(false);
        setAiLiveHtml('');
        setAiMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: errMsg || 'Something went wrong. Please try again.' };
          return updated;
        });
      },
    );
  }

  function handleAiFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAiAttachment({ name: file.name, dataUrl: reader.result as string });
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function toggleVoiceInput() {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast({ title: 'Voice input not supported', description: 'Try Chrome or Edge.', variant: 'destructive' });
      return;
    }
    if (isListening) {
      speechRef.current?.stop();
      setIsListening(false);
      return;
    }
    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e: any) => {
      const transcript = e.results[0][0].transcript;
      setAiInput((prev) => prev ? `${prev} ${transcript}` : transcript);
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    speechRef.current = rec;
    rec.start();
    setIsListening(true);
  }

  async function handleSave() {
    const editor = editorRef.current;
    if (!editor) return;
    setSaving(true);
    const content = editor.getProjectData();
    const res = await wbPagesApi.update(siteId!, pageId!, { content: content as any });
    setSaving(false);
    if (res.error) {
      showApiError(res, { fallback: { title: 'Save failed' } });
      return;
    }
    toast({ variant: "success", title: 'Saved!' });
    setPage((res.data as any)?.data ?? res.data);
  }

  async function handlePublish() {
    const editor = editorRef.current;
    if (!editor) return;

    setSaving(true);
    const content = editor.getProjectData();
    const saveRes = await wbPagesApi.update(siteId!, pageId!, { content: content as any });
    if (saveRes.error) {
      setSaving(false);
      showApiError(saveRes, { fallback: { title: 'Save failed' } });
      return;
    }

    const res = await wbPagesApi.update(siteId!, pageId!, { is_published: !page?.is_published });
    setSaving(false);
    if (res.error) {
      showApiError(res, { fallback: { title: 'Error' } });
      return;
    }
    const updated = (res.data as any)?.data ?? res.data;
    setPage(updated);
    toast({ variant: "success", title: updated?.is_published ? 'Published!' : 'Unpublished' });
  }

  // ── AI-code mode: hand off to the dedicated editor ─────────────────────────
  if (!loading && pageMode === 'ai-code' && site && page) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen"><Loader2 className="h-7 w-7 animate-spin" /></div>}>
        {/*
          key={page.id} forces a fresh AiCodeEditor instance whenever the
          loaded page actually changes. The editor seeds its `project` /
          `bundle` from `page.page_code` via one-time lazy useState
          initializers (AiCodeEditor.tsx) — without a key, React reuses the
          same instance across sibling-page navigation and those initializers
          never re-run, so every page rendered the FIRST-opened page's code +
          preview. We key on `page.id` (NOT the `pageId` route param) on
          purpose: the param changes before the [siteId, pageId] fetch
          resolves, so keying on the param would remount against the stale
          `page` prop and re-introduce the same bug. `page.id` only flips
          after setPage(newPage) lands.
        */}
        <AiCodeEditor
          key={page.id}
          site={site}
          page={page}
          siteId={siteId!}
          pageId={pageId!}
          onBack={() => navigate('/home/website-builder')}
          onPageUpdate={(updated) => setPage(updated)}
        />
      </Suspense>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden relative" style={{ height: '100vh' }}>
      <style>{GJS_CSS}</style>

      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm">
          <Loader2 className="h-7 w-7 animate-spin text-violet-300" />
        </div>
      )}

      <header className="flex-shrink-0 bg-card border-b border-border flex items-center px-3 gap-2 z-10" style={{ height: 52 }}>
        <button
          onClick={() => navigate('/home/website-builder')}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-lg px-2 py-1.5 hover:bg-muted flex-shrink-0"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm hidden sm:inline">Back</span>
        </button>

        <div className="flex items-center gap-1.5 text-sm flex-shrink-0">
          <span className="text-muted-foreground truncate max-w-[100px]">{site?.name ?? '—'}</span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60 flex-shrink-0" />
          <span className="text-foreground font-semibold truncate max-w-[140px]">{page?.title ?? '—'}</span>
        </div>

        {/* Mode toggle */}
        <div className="flex items-center rounded-lg border border-border bg-muted/40 p-0.5 gap-0.5 ml-2 flex-shrink-0">
          <button
            onClick={() => setPageMode('static')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${pageMode === 'static' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Visual
          </button>
          <button
            onClick={() => setPageMode('ai-code')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${pageMode === 'ai-code' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Code2 className="h-3.5 w-3.5" /> AI Code
          </button>
        </div>

        <div className="flex-1" />

        {!isPreviewMode && (
          <div className="flex items-center bg-muted rounded-xl p-1 gap-0.5 flex-shrink-0 border border-border">
            {([
              { id: 'Desktop', icon: <Monitor className="h-4 w-4" /> },
              { id: 'Tablet', icon: <Tablet className="h-4 w-4" /> },
              { id: 'Mobile portrait', icon: <Smartphone className="h-4 w-4" /> },
            ] as const).map(({ id, icon }) => (
              <button
                key={id}
                onClick={() => switchDevice(id)}
                title={id}
                className={`px-2.5 py-1.5 rounded-lg transition-all flex items-center justify-center ${
                  device === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {icon}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1" />

        {!isPreviewMode && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button onClick={() => editorRef.current?.UndoManager.undo()} title="Undo" className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <Undo2 className="h-4 w-4" />
            </button>
            <button onClick={() => editorRef.current?.UndoManager.redo()} title="Redo" className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <Redo2 className="h-4 w-4" />
            </button>
          </div>
        )}

        {!isPreviewMode && <div className="w-px h-5 bg-border flex-shrink-0 mx-1" />}

        {!isPreviewMode && page && (
          <Badge variant={page.is_published ? 'default' : 'secondary'} className={`text-xs flex-shrink-0 ${page.is_published ? 'bg-[hsl(var(--success-subtle))] text-[hsl(var(--success))] border-[hsl(var(--success))]' : 'bg-muted text-muted-foreground border-border'}`}>
            {page.is_published ? 'Published' : 'Draft'}
          </Badge>
        )}

        <Button
          size="sm"
          variant="outline"
          onClick={handleTogglePreview}
          className={`flex-shrink-0 h-8 text-xs ${
            isPreviewMode
              ? 'bg-primary text-primary-foreground border-primary hover:bg-[hsl(var(--primary-hover))]'
              : 'bg-background border-border text-foreground hover:bg-muted'
          }`}
        >
          {isPreviewMode ? <EyeOff className="h-3.5 w-3.5 mr-1.5" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
          {isPreviewMode ? 'Exit Preview' : 'Preview'}
        </Button>

        {!isPreviewMode && (
          <Button size="sm" variant="outline" onClick={handleSave} disabled={saving} className="flex-shrink-0 h-8 text-xs bg-background border-border text-foreground hover:bg-muted">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
        )}

        {!isPreviewMode && (
          <Button
            size="sm"
            onClick={handlePublish}
            disabled={saving}
            className={`flex-shrink-0 h-8 text-xs font-semibold ${page?.is_published ? 'bg-background border border-border text-[hsl(var(--success))] hover:bg-muted' : 'bg-primary hover:bg-[hsl(var(--primary-hover))] text-primary-foreground border-0'}`}
            variant={page?.is_published ? 'outline' : 'default'}
          >
            {page?.is_published ? 'Unpublish' : 'Publish'}
          </Button>
        )}
      </header>

      <div className="flex min-h-0" style={{ flex: 1 }}>
        {!isPreviewMode && (
          <div className="flex-shrink-0 bg-card border-r border-border flex flex-col items-center py-2 gap-0.5" style={{ width: 60 }}>
            {([
              { id: 'blocks' as LeftPanelId, icon: <LayoutGrid className="h-[18px] w-[18px]" />, label: 'Blocks' },
              { id: 'global' as LeftPanelId, icon: <Palette className="h-[18px] w-[18px]" />, label: 'Styles' },
              { id: 'pages' as LeftPanelId, icon: <FileStack className="h-[18px] w-[18px]" />, label: 'Pages' },
              { id: 'layers' as LeftPanelId, icon: <Layers className="h-[18px] w-[18px]" />, label: 'Layers' },
              { id: 'assets' as LeftPanelId, icon: <ImageIcon className="h-[18px] w-[18px]" />, label: 'Assets' },
              { id: 'ai' as LeftPanelId, icon: <Sparkles className="h-[18px] w-[18px]" />, label: 'AI' },
            ]).map((item) => (
              <button
                key={item.id}
                onClick={() => setActivePanel(item.id)}
                title={item.label}
                className={`w-full py-2 px-1 rounded-xl flex flex-col items-center justify-center gap-1 transition-all ${
                  activePanel === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {item.icon}
                <span className="text-[9px] leading-none font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        )}

        {!isPreviewMode && (
          <div className="flex-shrink-0 bg-card border-r border-border overflow-hidden" style={{ width: 250 }}>
          <div style={{ display: activePanel === 'blocks' ? 'flex' : 'none', flexDirection: 'column', height: '100%' }}>
            <div className="flex-shrink-0 px-2 py-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search blocks..."
                  value={blockSearch}
                  onChange={(e) => setBlockSearch(e.target.value)}
                  className="w-full h-7 pl-7 pr-6 text-xs bg-muted rounded-md border border-border focus:outline-none focus:ring-1 focus:ring-ring text-foreground placeholder:text-muted-foreground"
                />
                {blockSearch && (
                  <button onClick={() => setBlockSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            <div id="gjs-blocks-panel" style={{ flex: 1, overflowY: 'auto' }} />
          </div>
          <div
            id="gjs-global-panel"
            style={{ display: activePanel === 'global' ? 'block' : 'none', height: '100%', overflowY: 'auto' }}
            className="p-3 text-sm space-y-3"
          >
            <h3 className="text-sm font-semibold text-foreground">Global Styles</h3>

            <details open className="border border-border rounded-md">
              <summary className="cursor-pointer px-2.5 py-2 text-xs font-semibold text-muted-foreground">Colors</summary>
              <div className="px-2.5 pb-2.5 space-y-2">
                {([
                  ['primaryColor', 'Primary'],
                  ['secondaryColor', 'Secondary'],
                  ['accentColor', 'Accent'],
                  ['successColor', 'Success'],
                  ['warningColor', 'Warning'],
                  ['errorColor', 'Error'],
                ] as Array<[keyof GlobalStyles, string]>).map(([key, label]) => (
                  <div key={String(key)}>
                    {renderColorControl(label, key as ColorStyleKey)}
                  </div>
                ))}
              </div>
            </details>

            <details open className="border border-border rounded-md">
              <summary className="cursor-pointer px-2.5 py-2 text-xs font-semibold text-muted-foreground">Body</summary>
              <div className="px-2.5 pb-2.5 space-y-2">
                {renderColorControl('Background', 'bodyBackground')}
                {renderColorControl('Color', 'bodyColor')}
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Font Size ({globalStyles.bodyFontSize}px)</label><input type="range" min={12} max={24} step={1} value={globalStyles.bodyFontSize} onChange={(e) => updateGlobalStyles('bodyFontSize', Number(e.target.value))} className="w-full" /></div>
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Line Height ({globalStyles.bodyLineHeight})</label><input type="range" min={1.1} max={2} step={0.05} value={globalStyles.bodyLineHeight} onChange={(e) => updateGlobalStyles('bodyLineHeight', Number(e.target.value))} className="w-full" /></div>
                <select value={globalStyles.bodyFontFamily} onChange={(e) => updateGlobalStyles('bodyFontFamily', e.target.value)} className="w-full h-8 rounded border border-border bg-background px-2 text-xs text-foreground">
                  <option value="DM Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">DM Sans</option>
                  <option value="Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Inter</option>
                  <option value="Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Poppins</option>
                  <option value="Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Montserrat</option>
                </select>
              </div>
            </details>

            <details open className="border border-border rounded-md">
              <summary className="cursor-pointer px-2.5 py-2 text-xs font-semibold text-muted-foreground">Heading</summary>
              <div className="px-2.5 pb-2.5 space-y-2">
                {renderColorControl('Color', 'headingColor')}
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Font Size ({globalStyles.headingFontSize}px)</label><input type="range" min={28} max={80} step={1} value={globalStyles.headingFontSize} onChange={(e) => updateGlobalStyles('headingFontSize', Number(e.target.value))} className="w-full" /></div>
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Line Height ({globalStyles.headingLineHeight})</label><input type="range" min={1} max={1.6} step={0.05} value={globalStyles.headingLineHeight} onChange={(e) => updateGlobalStyles('headingLineHeight', Number(e.target.value))} className="w-full" /></div>
                <select value={globalStyles.headingFontFamily} onChange={(e) => updateGlobalStyles('headingFontFamily', e.target.value)} className="w-full h-8 rounded border border-border bg-background px-2 text-xs text-foreground">
                  <option value="DM Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">DM Sans</option>
                  <option value="Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Inter</option>
                  <option value="Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Poppins</option>
                  <option value="Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Montserrat</option>
                </select>
              </div>
            </details>

            <details className="border border-border rounded-md">
              <summary className="cursor-pointer px-2.5 py-2 text-xs font-semibold text-muted-foreground">Subheading</summary>
              <div className="px-2.5 pb-2.5 space-y-2">
                {renderColorControl('Color', 'subheadingColor')}
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Font Size ({globalStyles.subheadingFontSize}px)</label><input type="range" min={20} max={56} step={1} value={globalStyles.subheadingFontSize} onChange={(e) => updateGlobalStyles('subheadingFontSize', Number(e.target.value))} className="w-full" /></div>
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Line Height ({globalStyles.subheadingLineHeight})</label><input type="range" min={1} max={1.8} step={0.05} value={globalStyles.subheadingLineHeight} onChange={(e) => updateGlobalStyles('subheadingLineHeight', Number(e.target.value))} className="w-full" /></div>
                <select value={globalStyles.subheadingFontFamily} onChange={(e) => updateGlobalStyles('subheadingFontFamily', e.target.value)} className="w-full h-8 rounded border border-border bg-background px-2 text-xs text-foreground">
                  <option value="DM Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">DM Sans</option>
                  <option value="Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Inter</option>
                  <option value="Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Poppins</option>
                  <option value="Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">Montserrat</option>
                </select>
              </div>
            </details>

            <details className="border border-border rounded-md">
              <summary className="cursor-pointer px-2.5 py-2 text-xs font-semibold text-muted-foreground">Buttons</summary>
              <div className="px-2.5 pb-2.5 space-y-2">
                {renderColorControl('Background', 'buttonBackground')}
                {renderColorControl('Color', 'buttonColor')}
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Border Radius ({globalStyles.buttonRadius}px)</label><input type="range" min={0} max={9999} step={1} value={globalStyles.buttonRadius} onChange={(e) => updateGlobalStyles('buttonRadius', Number(e.target.value))} className="w-full" /></div>
              </div>
            </details>

            <details className="border border-border rounded-md">
              <summary className="cursor-pointer px-2.5 py-2 text-xs font-semibold text-muted-foreground">Links</summary>
              <div className="px-2.5 pb-2.5 space-y-2">
                {renderColorControl('Color', 'linkColor')}
                <select value={globalStyles.linkDecoration} onChange={(e) => updateGlobalStyles('linkDecoration', e.target.value as 'none' | 'underline')} className="w-full h-8 rounded border border-border bg-background px-2 text-xs text-foreground">
                  <option value="none">No decoration</option>
                  <option value="underline">Underline</option>
                </select>
              </div>
            </details>

            <details className="border border-border rounded-md">
              <summary className="cursor-pointer px-2.5 py-2 text-xs font-semibold text-muted-foreground">Borders</summary>
              <div className="px-2.5 pb-2.5 space-y-2">
                <div className="space-y-1"><label className="text-xs text-muted-foreground">Border Radius ({globalStyles.borderRadius}px)</label><input type="range" min={0} max={32} step={1} value={globalStyles.borderRadius} onChange={(e) => updateGlobalStyles('borderRadius', Number(e.target.value))} className="w-full" /></div>
                {renderColorControl('Border Color', 'borderColor')}
              </div>
            </details>
          </div>
          <div
            id="gjs-pages-panel"
            style={{ display: activePanel === 'pages' ? 'block' : 'none', height: '100%', overflowY: 'auto' }}
            className="p-3 text-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Pages</span>
              <div className="flex items-center gap-1">
                {/* New page from template — primary action. Opens the gallery
                    in a modal with addToSiteId set so the new pages are added
                    to THIS site rather than creating a fresh site. */}
                <button
                  onClick={() => setPageTemplateModalOpen(true)}
                  className="h-7 px-2 rounded-md bg-primary text-primary-foreground inline-flex items-center justify-center gap-1 hover:bg-[hsl(var(--primary-hover))] text-[11px] font-medium"
                  title="New page from template"
                  data-testid="wb-editor-new-page-from-template"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Template
                </button>
                {/* Existing blank-page add. */}
                <button
                  onClick={handleAddPage}
                  className="h-7 w-7 rounded-md border border-border bg-background text-foreground inline-flex items-center justify-center hover:bg-muted"
                  title="Add blank page"
                  data-testid="wb-editor-new-page-blank"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="space-y-1">
              {pages.map((p) => (
                <div
                  key={p.id}
                  className={`w-full rounded-md border transition-colors flex items-center gap-1 ${
                    activePageId === p.id
                      ? 'bg-accent border-[hsl(var(--ring))] text-accent-foreground'
                      : 'bg-background border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <button
                    onClick={() => handleSelectPage(p.id)}
                    className="flex-1 text-left px-2.5 py-2 truncate"
                    title={p.name}
                  >
                    {p.name}
                  </button>
                  <button
                    onClick={() => handleDeletePage(p.id, p.name)}
                    className="h-7 w-7 mr-1 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    title={`Delete ${p.name}`}
                    aria-label={`Delete ${p.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div id="gjs-layers-panel" style={{ display: activePanel === 'layers' ? 'block' : 'none', height: '100%', overflowY: 'auto' }} />
          <div id="gjs-assets-panel" style={{ display: activePanel === 'assets' ? 'block' : 'none', height: '100%', overflowY: 'auto' }} />

          {/* AI Chat Panel */}
          <div style={{ display: activePanel === 'ai' ? 'flex' : 'none', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div className="flex-shrink-0 px-3 py-2.5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs font-semibold text-foreground">AI Assistant</p>
              </div>
              {aiMessages.length > 0 && (
                <button
                  onClick={() => setAiMessages([])}
                  className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Clear chat"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-4 min-h-0">
              {aiMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-2">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <Wand2 className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-foreground">AI Page Designer</p>
                    <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">Describe the page you want — include business type, sections, colors, and style.</p>
                  </div>
                  <div className="w-full space-y-1.5 mt-1">
                    {[
                      'Landing page for a coffee shop with dark navy theme',
                      'Portfolio site for a photographer, minimal white',
                      'SaaS pricing page with 3 plan tiers',
                    ].map((ex) => (
                      <button
                        key={ex}
                        onClick={() => setAiInput(ex)}
                        className="w-full text-left text-[10px] px-2 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors leading-relaxed"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {aiMessages.map((msg, i) => {
                const isStreamingThis = aiLoading && i === aiMessages.length - 1 && msg.role === 'assistant';
                const linesWritten = msg.content ? msg.content.split('\n').length : 0;
                const streamingIsHtml = isStreamingThis && msg.content.trimStart().startsWith('<');
                return (
                <div key={i} className={`flex flex-col gap-1.5 ${msg.role === 'user' ? 'items-end' : 'items-start'} w-full`}>
                  {msg.attachment && (
                    <img src={msg.attachment} alt="attachment" className="max-w-[90%] rounded-xl border border-border object-cover" style={{ maxHeight: 120 }} />
                  )}

                  {/* HTML result card */}
                  {(msg.isHtml || streamingIsHtml) ? (
                    <div className="w-full rounded-xl border border-border bg-muted/40 px-3 py-2.5">
                      <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        {isStreamingThis
                          ? <><Loader2 className="h-3 w-3 animate-spin" /><span>Building… {linesWritten} lines</span></>
                          : <><Sparkles className="h-3 w-3 text-green-500" /><span className="text-foreground">Page applied to canvas — {linesWritten} lines</span></>
                        }
                      </span>
                    </div>
                  ) : (
                    /* Plain text bubble */
                    <div className={`max-w-[90%] rounded-2xl px-3 py-2 text-[11px] leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap'
                        : 'bg-muted text-foreground rounded-bl-sm'
                    }`}>
                      {isStreamingThis
                        ? <span className="whitespace-pre-wrap">{msg.content}<span className="inline-block w-1.5 h-3 bg-foreground/60 ml-0.5 animate-pulse rounded-sm" /></span>
                        : <span className="whitespace-pre-wrap">{msg.content}</span>
                      }
                    </div>
                  )}
                </div>
                );
              })}
              <div ref={aiMessagesEndRef} />
            </div>

            {/* Input */}
            <div className="flex-shrink-0 border-t border-border p-2 space-y-2">
              {/* Attachment preview */}
              {aiAttachment && (
                <div className="flex items-center gap-2 px-1">
                  <img src={aiAttachment.dataUrl} alt={aiAttachment.name} className="h-10 w-10 rounded-lg object-cover border border-border flex-shrink-0" />
                  <span className="text-[10px] text-muted-foreground truncate flex-1">{aiAttachment.name}</span>
                  <button onClick={() => setAiAttachment(null)} className="text-muted-foreground hover:text-foreground flex-shrink-0">
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Textarea */}
              <Textarea
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiSend(); }
                }}
                placeholder="Ask anything..."
                rows={3}
                className="resize-none text-[11px] w-full bg-muted border-border text-foreground placeholder:text-muted-foreground rounded-xl"
                disabled={aiLoading}
              />

              {/* Bottom action row */}
              <div className="flex items-center justify-between px-0.5">
                <div className="flex items-center gap-1">
                  {/* File attach */}
                  <input ref={aiFileRef} type="file" accept="image/*" className="hidden" onChange={handleAiFileSelect} />
                  <button
                    onClick={() => aiFileRef.current?.click()}
                    className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title="Attach image"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                  </button>

                  {/* Voice input */}
                  <button
                    onClick={toggleVoiceInput}
                    className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
                      isListening
                        ? 'text-red-500 bg-red-500/10 animate-pulse'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    title={isListening ? 'Stop listening' : 'Voice input'}
                  >
                    {isListening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                  </button>
                </div>

                {/* Send button — circular */}
                <button
                  onClick={handleAiSend}
                  disabled={(!aiInput.trim() && !aiAttachment) || aiLoading}
                  className="h-8 w-8 rounded-full bg-primary hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed text-primary-foreground flex items-center justify-center transition-opacity"
                >
                  {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          </div>
        )}

        <div className="flex-1 min-w-0 overflow-hidden relative" style={{ backgroundColor: 'hsl(var(--surface-container))' }}>
          <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
          {canvasIsEmpty && !isPreviewMode && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-center select-none">
                <div className="w-16 h-16 rounded-2xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-sm flex items-center justify-center">
                  <LayoutGrid className="h-7 w-7 text-muted-foreground/60" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground/70">Start building your page</p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">Drag blocks from the left panel onto the canvas</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {!isPreviewMode && (
          <div className="flex-shrink-0 bg-card border-l border-border flex flex-col overflow-hidden" style={{ width: 250 }}>
          <div className="flex gap-1 p-2 bg-muted/50 border-b border-border flex-shrink-0">
            {([
              { id: 'styles' as RightPanelId, label: 'Styles' },
              { id: 'traits' as RightPanelId, label: 'Properties' },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveRightPanel(tab.id)}
                className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  activeRightPanel === tab.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div id="gjs-styles-panel" style={{ display: activeRightPanel === 'styles' ? 'block' : 'none', flex: 1, overflowY: 'auto' }} />
          <div id="gjs-traits-panel" style={{ display: activeRightPanel === 'traits' ? 'block' : 'none', flex: 1, overflowY: 'auto' }} />
          </div>
        )}
      </div>

      {/* "+ New page from template" modal — fed addToSiteId={siteId} so the
          gallery's instantiate flow adds pages to THIS site instead of
          creating a fresh one. On completion we navigate to the new page's
          URL within the same editor. */}
      {pageTemplateModalOpen && siteId && (
        <div
          className="fixed inset-0 z-40 bg-white overflow-y-auto"
          role="dialog"
          aria-modal="true"
          data-testid="wb-editor-template-modal"
        >
          <TemplateGallery
            addToSiteId={siteId}
            onComplete={(newSiteId, newPageId) => {
              setPageTemplateModalOpen(false);
              navigate(`/home/website-builder/sites/${newSiteId}/editor/${newPageId}`);
            }}
            onClose={() => setPageTemplateModalOpen(false)}
          />
        </div>
      )}

    </div>
  );
}
